import _defineProperty from "/app/workspaces/v_1/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// @ts-check

import { create } from '@symbiotejs/symbiote';
import { ActivityBlock } from '../../abstract/ActivityBlock.js';
import { UploaderBlock } from '../../abstract/UploaderBlock.js';
import { stringToArray } from '../../utils/stringToArray.js';
import { wildcardRegexp } from '../../utils/wildcardRegexp.js';
import { buildStyles } from './buildStyles.js';
import { registerMessage, unregisterMessage } from './messages.js';
import { queryString } from './query-string.js';

/** @typedef {{ externalSourceType: string }} ActivityParams */

/**
 * @typedef {{
 *   type: 'file-selected';
 *   obj_type: 'selected_file';
 *   filename: string;
 *   url: string;
 *   alternatives?: Record<string, string>;
 * }} SelectedFileMessage
 */

/**
 * @typedef {{
 *   type: 'embed-css';
 *   style: string;
 * }} EmbedCssMessage
 */

/** @typedef {SelectedFileMessage | EmbedCssMessage} Message */

export class ExternalSource extends UploaderBlock {
  constructor() {
    super();
    _defineProperty(this, "couldBeCtxOwner", true);
    _defineProperty(this, "activityType", ActivityBlock.activities.EXTERNAL);
    /**
     * @private
     * @type {HTMLIFrameElement | null}
     */
    _defineProperty(this, "_iframe", null);
    _defineProperty(this, "updateCssData", () => {
      if (this.isActivityActive) {
        this._inheritedUpdateCssData();
        this.applyStyles();
      }
    });
    /** @private */
    _defineProperty(this, "_inheritedUpdateCssData", this.updateCssData);
    this.init$ = _objectSpread(_objectSpread({}, this.init$), {}, {
      activityIcon: '',
      activityCaption: '',
      selectedList: [],
      counter: 0,
      multiple: false,
      onDone: () => {
        for (const message of this.$.selectedList) {
          const url = this.extractUrlFromMessage(message);
          const {
            filename
          } = message;
          const {
            externalSourceType
          } = this.activityParams;
          this.addFileFromUrl(url, {
            fileName: filename,
            source: externalSourceType
          });
        }
        this.$['*currentActivity'] = ActivityBlock.activities.UPLOAD_LIST;
      },
      onCancel: () => {
        this.historyBack();
      }
    });
  }
  initCallback() {
    super.initCallback();
    this.registerActivity(this.activityType, {
      onActivate: () => {
        let {
          externalSourceType
        } = /** @type {ActivityParams} */this.activityParams;
        this.set$({
          activityCaption: "".concat(externalSourceType === null || externalSourceType === void 0 ? void 0 : externalSourceType[0].toUpperCase()).concat(externalSourceType === null || externalSourceType === void 0 ? void 0 : externalSourceType.slice(1)),
          activityIcon: externalSourceType
        });
        this.mountIframe();
      }
    });
    this.sub('*currentActivity', val => {
      if (val !== this.activityType) {
        this.unmountIframe();
      }
    });
    this.sub('selectedList', list => {
      this.$.counter = list.length;
    });
    this.subConfigValue('multiple', multiple => {
      this.$.multiple = multiple;
    });
  }

  /**
   * @private
   * @param {SelectedFileMessage} message
   */
  extractUrlFromMessage(message) {
    if (message.alternatives) {
      const preferredTypes = stringToArray(this.cfg.externalSourcesPreferredTypes);
      for (const preferredType of preferredTypes) {
        const regexp = wildcardRegexp(preferredType);
        for (const [type, typeUrl] of Object.entries(message.alternatives)) {
          if (regexp.test(type)) {
            return typeUrl;
          }
        }
      }
    }
    return message.url;
  }

  /**
   * @private
   * @param {Message} message
   */
  sendMessage(message) {
    var _this$_iframe;
    (_this$_iframe = this._iframe) === null || _this$_iframe === void 0 || (_this$_iframe = _this$_iframe.contentWindow) === null || _this$_iframe === void 0 || _this$_iframe.postMessage(JSON.stringify(message), '*');
  }

  /**
   * @private
   * @param {SelectedFileMessage} message
   */
  async handleFileSelected(message) {
    if (!this.$.multiple && this.$.selectedList.length) {
      return;
    }
    this.$.selectedList = [...this.$.selectedList, message];
    if (!this.$.multiple) {
      this.$.onDone();
    }
  }

  /** @private */
  handleIframeLoad() {
    this.applyStyles();
  }
  /**
   * @private
   * @param {string} propName
   */
  getCssValue(propName) {
    let style = window.getComputedStyle(this);
    return style.getPropertyValue(propName).trim();
  }

  /** @private */
  applyStyles() {
    let colors = {
      backgroundColor: this.getCssValue('--clr-background-light'),
      textColor: this.getCssValue('--clr-txt'),
      shadeColor: this.getCssValue('--clr-shade-lv1'),
      linkColor: '#157cfc',
      linkColorHover: '#3891ff'
    };
    this.sendMessage({
      type: 'embed-css',
      style: buildStyles(colors)
    });
  }

  /** @private */
  remoteUrl() {
    var _this$getCssData;
    let pubkey = this.cfg.pubkey;
    let imagesOnly = false.toString();
    let {
      externalSourceType
    } = this.activityParams;
    let params = {
      lang: ((_this$getCssData = this.getCssData('--l10n-locale-name')) === null || _this$getCssData === void 0 || (_this$getCssData = _this$getCssData.split('-')) === null || _this$getCssData === void 0 ? void 0 : _this$getCssData[0]) || 'en',
      public_key: pubkey,
      images_only: imagesOnly,
      pass_window_open: false,
      session_key: this.cfg.remoteTabSessionKey
    };
    let url = new URL(this.cfg.socialBaseUrl);
    url.pathname = "/window3/".concat(externalSourceType);
    url.search = queryString(params);
    return url.toString();
  }

  /** @private */
  mountIframe() {
    /** @type {HTMLIFrameElement} */
    // @ts-ignore
    let iframe = create({
      tag: 'iframe',
      attributes: {
        src: this.remoteUrl(),
        marginheight: 0,
        marginwidth: 0,
        frameborder: 0,
        allowTransparency: true
      }
    });
    iframe.addEventListener('load', this.handleIframeLoad.bind(this));
    this.ref.iframeWrapper.innerHTML = '';
    this.ref.iframeWrapper.appendChild(iframe);
    registerMessage('file-selected', iframe.contentWindow, this.handleFileSelected.bind(this));
    this._iframe = iframe;
    this.$.selectedList = [];
  }

  /** @private */
  unmountIframe() {
    this._iframe && unregisterMessage('file-selected', this._iframe.contentWindow);
    this.ref.iframeWrapper.innerHTML = '';
    this._iframe = null;
    this.$.selectedList = [];
    this.$.counter = 0;
  }
}
ExternalSource.template = /* HTML */"\n  <lr-activity-header>\n    <button type=\"button\" class=\"mini-btn\" set=\"onclick: *historyBack\">\n      <lr-icon name=\"back\"></lr-icon>\n    </button>\n    <div>\n      <lr-icon set=\"@name: activityIcon\"></lr-icon>\n      <span>{{activityCaption}}</span>\n    </div>\n    <button type=\"button\" class=\"mini-btn close-btn\" set=\"onclick: *historyBack\">\n      <lr-icon name=\"close\"></lr-icon>\n    </button>\n  </lr-activity-header>\n  <div class=\"content\">\n    <div ref=\"iframeWrapper\" class=\"iframe-wrapper\"></div>\n    <div class=\"toolbar\">\n      <button type=\"button\" class=\"cancel-btn secondary-btn\" set=\"onclick: onCancel\" l10n=\"cancel\"></button>\n      <div></div>\n      <div set=\"@hidden: !multiple\" class=\"selected-counter\"><span l10n=\"selected-count\"></span>{{counter}}</div>\n      <button type=\"button\" class=\"done-btn primary-btn\" set=\"onclick: onDone; @disabled: !counter\">\n        <lr-icon name=\"check\"></lr-icon>\n      </button>\n    </div>\n  </div>\n";